import React, { FC, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Container,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { originalIcons } from '@shared/components/icons/icons'
import { RegisteredStatus } from '@modules/requirement/requirement-table/requirements-table.component'
import { Roles } from '@shared/types'
import { useStore } from '@shared/hooks/use-store.hook'
import {
  Actions,
  ViewRowTableField,
  ViewRowTableFieldPropertyEnum,
} from '@shared/lib/reducer'
import { ACL } from '@shared/components/acl.component'
import { ShowConditionally } from '@shared/components/show-conditionally.component'
import { UnstyledLink } from '@shared/components/unstyled-link.component'
import { TableRequirement } from '@modules/company-profile/requirements-table.component'
import { useIsClientBasic } from '@shared/hooks/use-is-client-basic'
import ids from '@shared/onboarding/ids'

interface Props {
  registeredStatuses: RegisteredStatus[]
  activeFilters: number[]
  setActiveFilters: (value: number[]) => void
  setFilteredData: (value: TableRequirement[] | null) => void
  data: TableRequirement[]
  initiallyCollapsedWithButtons?: boolean
  expandAll: () => void
  collapseAll: () => void
  isExpanded: boolean
  isPreviewMode?: boolean
  hideFilters?: boolean
  auditID?: number
  type?: 'floating' | 'normal'
  currentScopeID?: number
  isActive?: boolean
}

const isDev =
  window.location.origin.includes(
    'https://dev-ecoms-audyty.azurewebsites.net'
  ) || process.env.NODE_ENV === 'development'

const INDICATOR_HEIGHT = 44
const HEADER_HEIGHT = 64
const TOP_EDGE = isDev ? INDICATOR_HEIGHT + HEADER_HEIGHT : HEADER_HEIGHT

const buildActsUrl = (currentScopeID: number) => {
  const pathname = window.location.pathname
  return `${pathname}/acts/${currentScopeID}`
}

export const FilterBar = (props: Props) => {
  const {
    registeredStatuses,
    setFilteredData,
    data,
    activeFilters,
    setActiveFilters,
    isExpanded,
    initiallyCollapsedWithButtons,
    expandAll,
    collapseAll,
    isPreviewMode,
    hideFilters = false,
    currentScopeID,
    isActive,
  } = props
  const classes = useStyles()
  const isClientBasic = useIsClientBasic()
  const { state, dispatch } = useStore()

  const viewRowTableFields = state.viewRowTableFields as ViewRowTableField[]
  const clientBasicRowTableField = state.viewRowTableFields.filter(
    (field: { property: string }) =>
      field.property === ViewRowTableFieldPropertyEnum.LEGAL_REQUIREMENT
  )

  const fieldsToRender = isClientBasic
    ? clientBasicRowTableField
    : viewRowTableFields

  const menuItemsToRender = !isActive
    ? clientBasicRowTableField
    : fieldsToRender

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClickOpenViewOptions = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseViewOptions = () => {
    setAnchorEl(null)
  }

  const switchFilter = (filterId: number | null) => {
    if (filterId) {
      const newFilters = activeFilters.includes(filterId)
        ? [...activeFilters.filter((id) => id !== filterId)]
        : [...activeFilters, filterId]
      setActiveFilters(newFilters)

      if (newFilters.length < 1) return setFilteredData(null)

      const updatedData = data.filter((requirement) => {
        return (
          requirement.auditorStatus &&
          newFilters.includes(requirement.auditorStatus.statusBaseId) &&
          (state.isDraftFilterActive ? requirement.isDraft : true)
        )
      })

      return setFilteredData(updatedData)
    }
    setActiveFilters([])
    setFilteredData(null)
  }

  const toggleViewRowTableField = (property: string) => {
    dispatch({
      type: Actions.TOGGLE_VIEW_ROW_TABLE_FIELD,
      payload: { property },
    })
  }

  const toggleFilterDrafts = () => {
    dispatch({ type: Actions.TOGGLE_FILTER_DRAFTS })
    if (!state.isDraftFilterActive) {
      expandAll()
    }
  }

  return (
    <div className={classes.barWrapper}>
      {hideFilters ? (
        <div />
      ) : (
        <div className={classes.filtersButtonsWrapper}>
          <span className={classes.filtersTitle}>
            Filtruj wg statusu audytora:
          </span>
          {registeredStatuses
            ?.sort((a, b) => {
              if (a.statusBaseID < b.statusBaseID) return -1
              if (a.statusBaseID > b.statusBaseID) return 1
              return 0
            })
            .map(({ statusBaseID, statusColor, name }) => (
              <Tooltip title={name.toUpperCase()} key={statusBaseID}>
                <div
                  onClick={() => switchFilter(statusBaseID)}
                  className={classes.colorCircleBtn}
                  style={{
                    backgroundColor: statusColor,
                    border: '1px solid #000',
                    boxShadow: activeFilters.includes(statusBaseID)
                      ? '0px 0px 0px 2px #000'
                      : 'none',
                  }}
                />
              </Tooltip>
            ))}
          <Tooltip title="Usuń filtry">
            <IconButton
              className={classes.refreshButton}
              onClick={() => switchFilter(null)}
              size="large"
            >
              <originalIcons.Close />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className={classes.buttonsWrapper}>
        {isPreviewMode && currentScopeID !== undefined && (
          <UnstyledLink to={buildActsUrl(currentScopeID)}>
            <Button variant="outlined" id={ids.ACTS_REGISTER}>
              Rejestr aktów
            </Button>
          </UnstyledLink>
        )}
        {!isClientBasic && (
          <Button
            aria-controls="simple-menu"
            aria-haspopup="true"
            variant="outlined"
            onClick={handleClickOpenViewOptions}
          >
            Widok
          </Button>
        )}

        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseViewOptions}
        >
          {menuItemsToRender?.map(
            ({ property, label, selected }: ViewRowTableField) => (
              <MenuItem
                key={property}
                value={property}
                onClick={() => toggleViewRowTableField(property)}
              >
                <Checkbox checked={selected} color="primary" />
                <ListItemText
                  primary={label}
                  classes={{ primary: classes.listItemText }}
                />
              </MenuItem>
            )
          )}
        </Menu>
        <ShowConditionally when={!isPreviewMode}>
          <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
            <div className={classes.collapseButtons}>
              <Button variant="outlined" onClick={toggleFilterDrafts}>
                {state.isDraftFilterActive ? 'Pokaż wszystko' : 'Pokaż drafty'}
              </Button>
            </div>
          </ACL>
        </ShowConditionally>
        {initiallyCollapsedWithButtons ? (
          <div className={classes.collapseButtons}>
            <Button
              variant="outlined"
              onClick={isExpanded ? collapseAll : expandAll}
            >
              {isExpanded ? 'Zwiń wszystkie' : 'Rozwiń wszystkie'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export const FilterStatusesComponent: FC<
  Props & { triggerRef?: React.RefObject<HTMLDivElement> }
> = (props) => {
  const classes = useStyles()

  return (
    <>
      {(props.type === 'normal' || props.type === 'floating') && (
        <div className={classes.filtersWrapper} ref={props.triggerRef}>
          <FilterBar {...props} />
        </div>
      )}
      {props.type === 'floating' && (
        <Box className={classes.floatingFiltersContainer} boxShadow={1}>
          <Container maxWidth="xl" className={classes.floatingFilters}>
            <FilterBar {...props} />
          </Container>
        </Box>
      )}
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  barWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listItemText: {
    fontSize: '0.8rem',
  },
  filtersWrapper: {
    display: 'flex',
    padding: '10px',
    paddingLeft: '24px',
    paddingTop: 0,
    marginTop: '-10px',
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 62.5,
  },
  filtersTitle: {
    color: theme.palette.text.disabled,
  },
  filtersButtonsWrapper: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  refreshButton: {
    marginLeft: -10,
    alignItems: 'center',
    cursor: 'pointer',
  },
  colorCircleBtn: {
    width: '25px',
    height: '25px',
    borderRadius: '50%',
    border: '1px solid #fff',
    cursor: 'pointer',
  },
  collapseButtons: {},
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  floatingFiltersContainer: {
    position: 'fixed',
    top: TOP_EDGE,
    backgroundColor: theme.palette.background.paper,
    zIndex: 9999,
    left: 0,
    right: 0,
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
    },
  },
  floatingFilters: {
    display: 'flex',
    paddingLeft: 40,
    paddingRight: 50,
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))
